// src/components/AuthPage.js
import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import firebase from "firebase/compat/app";

// Import fonts and CSS so the page matches your Plans page aesthetic
import "../fonts/bootstrap-icons.woff";
import "../fonts/bootstrap-icons.woff2";
import "bootstrap/dist/css/bootstrap.min.css";
import "../css/main.css";
import "../css/bootstrap-icons.css";

// Header Component (similar to your Plans header)
function Header() {
  return (
    <header className="site-header d-flex flex-column justify-content-center align-items-center">
      <div className="container">
        <div className="row align-items-center">
          <div className="col-lg-5 col-12">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/">Homepage</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                </li>
              </ol>
            </nav>
            <h2 className="text-white">Login / Sign Up</h2>
          </div>
        </div>
      </div>
    </header>
  );
}

// Auth Form Component styled similarly to your Plans blocks
function AuthForm() {
  const [isLogin, setIsLogin] = useState(true); // Toggle between login and signup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // Only for signup
  const [displayName, setDisplayName] = useState(""); // New field for display name
  const [birthday, setBirthday] = useState(""); // New field for birthday
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect if the user is already authenticated
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Email/Password Login or Sign Up
  const handleEmailAuth = async () => {
    setError(null);
    setLoading(true);

    if (!isLogin && password !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        // Login
        const userCredential = await auth.signInWithEmailAndPassword(
          email,
          password
        );
        localStorage.setItem("userUID", userCredential.user.uid);
      } else {
        // Sign Up
        const userCredential = await auth.createUserWithEmailAndPassword(
          email,
          password
        );
        await userCredential.user.sendEmailVerification();
        alert("A verification link has been sent to your email.");

        // Save user info in a new Firestore collection called "front-users"
        const userData = {
          email: userCredential.user.email,
          uid: userCredential.user.uid,
          createdAt: new Date(),
          displayName: displayName || userCredential.user.displayName, // Save display name
          birthday: birthday || "", // Save birthday
          photoURL: userCredential.user.photoURL || "",
        };

        await db.collection("front-users").doc(userCredential.user.uid).set(userData);
        localStorage.setItem("userUID", userCredential.user.uid);
      }
      navigate("/");
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  // Password Reset
  const handlePasswordReset = async () => {
    setError(null);
    if (!email) {
      setError("Please enter your email to reset your password.");
      return;
    }
    try {
      await auth.sendPasswordResetEmail(email);
      alert("Password reset email sent. Please check your inbox.");
    } catch (err) {
      setError(err.message);
    }
  };

  // Google Sign-In Handler
  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await auth.signInWithPopup(provider);
      localStorage.setItem("userUID", result.user.uid);

      const userData = {
        email: result.user.email,
        uid: result.user.uid,
        displayName: result.user.displayName || "",
        photoURL: result.user.photoURL || "",
        lastLogin: new Date(),
      };

      // Create or update the user document in "front-users"
      await db
        .collection("front-users")
        .doc(result.user.uid)
        .set(userData, { merge: true });
      navigate("/");
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  return (
    <section className="section-padding">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-12">
            <div className="custom-block bg-white shadow-lg p-4">
              <h3 className="mb-4 text-center">
                {isLogin ? "Login" : "Sign Up"}
              </h3>
              {error && <div className="alert alert-danger">{error}</div>}
              {!isLogin && (
                <>
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Display Name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <input
                      type="date"
                      className="form-control"
                      placeholder="Birthday"
                      value={birthday}
                      onChange={(e) => setBirthday(e.target.value)}
                    />
                  </div>
                </>
              )}
              <div className="mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="mb-3">
                <input
                  type="password"
                  className="form-control"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              {!isLogin && (
                <div className="mb-3">
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>
              )}
              <div className="d-grid mb-3">
                <button
                  className="btn custom-btn"
                  onClick={handleEmailAuth}
                  disabled={loading}
                >
                  {isLogin ? "Login" : "Sign Up"}
                </button>
              </div>
              <div className="d-grid mb-3">
                <button
                  className="btn btn-danger"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  Continue with Google
                </button>
              </div>
              {isLogin && (
                <p className="text-center">
                  <span
                    className="text-primary"
                    style={{ cursor: "pointer" }}
                    onClick={handlePasswordReset}
                  >
                    Forgot Password?
                  </span>
                </p>
              )}
              <p className="text-center">
                {isLogin
                  ? "Don't have an account?"
                  : "Already have an account?"}{" "}
                <span
                  className="text-primary"
                  style={{ cursor: "pointer" }}
                  onClick={() => setIsLogin(!isLogin)}
                >
                  {isLogin ? "Sign Up" : "Login"}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// A divider component placed above the footer
function Divider() {
  return (
    <div className="container">
      <hr className="my-5" />
    </div>
  );
}


// The main AuthPage component that puts everything together
export default function AuthPage() {
  return (
    <>
      <Header />
      <AuthForm />
      <Divider />
    </>
  );
}
